import { useCallback, useMemo } from "react";
import { 
  useCreateAbsenceEmployeeClosuresCrossReferenceMutation, 
  useCreateAbsenceMutation, 
  useDeleteAbsenceEmployeeClosuresCrossReferenceMutation, 
  useGetAbsenceEmployeeClosuresCrossReferencesQuery, 
  useGetAbsenceQuery, 
  useGetEmployeesQuery, 
  useUpdateAbsenceMutation,
  useGetEmployeeClosuresQuery as getEmployeeClosuresQuery
 } from "../../../../api/plamo-api";
import { AdvancedForm } from "../../form/advanced-form";
import { Form, Row, Space } from "antd";
import { useNavigate } from "react-router-dom";
import FormItem from "../../form/form-item";
import BackButton from "../../util/back-button";
import ConfirmButton from "../../util/confirm-button";
import PlamoApiWithSaveNotifications from "../../util/with-save-notification";

const {
  useDeleteAbsenceWithSaveNotificationMutation
} = PlamoApiWithSaveNotifications;

function EmployeeAbsenceDetails({ id, employeeId }) {
  const idInt = useMemo(() => id === 'new' ? 0 : parseInt(id), [id])
  const { data: absence } = useGetAbsenceQuery({ id: idInt }, { skip: !idInt }) as any;
  const navigate = useNavigate();
  const [createAbsence] = useCreateAbsenceMutation();
  const [updateAbsence] = useUpdateAbsenceMutation();
  const [createAbsenceEmployeeClosureCrossReference] = useCreateAbsenceEmployeeClosuresCrossReferenceMutation();
  const [deleteAbsenceEmployeeClosuresCrossReference] = useDeleteAbsenceEmployeeClosuresCrossReferenceMutation();
  const [deleteAbsence] = useDeleteAbsenceWithSaveNotificationMutation();
  const { data: closureCrossReferences } = useGetAbsenceEmployeeClosuresCrossReferencesQuery({ f: JSON.stringify({ "absence.id": { c: "EQ", v: idInt } }), st: JSON.stringify({ id: "desc" })}, { skip: !idInt}) as any;
  const [form] = Form.useForm();

  const absenceWithEmployeeClosures = useMemo(() => ({...(absence || { employeeId }), employeeClosureIds: closureCrossReferences?.items?.map(c => c.employeeClosureId)}), [absence, employeeId, closureCrossReferences])

  const getEmployeeClosuresQueryForEmployeeId = useCallback(({f, ...rest}) => {
    const existingFilter = JSON.parse(f || '{}');
    const newFilter = { ...existingFilter, "employee.id": { c: "EQ", v: employeeId}}
    return getEmployeeClosuresQuery({ f: JSON.stringify(newFilter), ...rest });
  }, [employeeId]);

  const onFinish = useCallback(async (values: any) => {
      let changedAbsenceId;
      if(!idInt) {
        const { data: newAbsence }:any = await createAbsence(values);
        console.log("Created new absence", newAbsence);
        changedAbsenceId = newAbsence.id;
      } else {
        const newAbsence = {...absence, ...values}
        await updateAbsence(newAbsence);
        changedAbsenceId = idInt;
      }
  
      const deletedEmployeeClosureCrossReferences = closureCrossReferences?.items?.filter(c => !values.employeeClosureIds || !values.employeeClosureIds.includes(c.employeeClosureId));
      //console.log("Deleted employee closure cross references", deletedEmployeeClosureCrossReferences, closureCrossReferences, values.employeeClosureIds);
  
      let promises = deletedEmployeeClosureCrossReferences?.map(c => deleteAbsenceEmployeeClosuresCrossReference(c)) || [];
      await Promise.all(promises);
  
      const existingEmployeeClosureCrossReferencesIds = closureCrossReferences?.items?.map(c => c.employeeClosureId);
      const createdEmployeeClosureCrossReferencesIds = (values.employeeClosureIds || []).filter(id => !existingEmployeeClosureCrossReferencesIds || !existingEmployeeClosureCrossReferencesIds.includes(id))
  
      promises = createdEmployeeClosureCrossReferencesIds.map(c => createAbsenceEmployeeClosureCrossReference({ absenceId: changedAbsenceId, employeeClosureId: c }))
      await Promise.all(promises);
  
      if(!idInt) {
        navigate(`./../${changedAbsenceId}`, { replace: true })
      }
    }, [idInt, createAbsence, updateAbsence, navigate, absence, closureCrossReferences, deleteAbsenceEmployeeClosuresCrossReference, createAbsenceEmployeeClosureCrossReference]);

    const onDelete = useCallback(async () => {
      await deleteAbsence({ id });
      navigate(-1);
    }, [id, navigate, deleteAbsence])

  return (<>
  <BackButton />
  <AdvancedForm name="absence" form={form} onFinish={onFinish} originalValues={absenceWithEmployeeClosures || { employeeId }} initialEditing={id === 'new'}>
    <Row><h3>Abwesenheit</h3></Row>
    <Row gutter={24}>
      <FormItem fieldName="employeeId" label="Mitarbeiter" readOnly type="ref" typeData={{ query: useGetEmployeesQuery, getId: employee => employee.id, render: employee => `${employee.firstName} ${employee.lastName}`}} />
      <FormItem fieldName="reason" label="Grund" type="enum" typeData={{ options: ["Urlaub", "Arbeitsunfähigkeit", "Arbeitsunf. Kind", "Sonstiges"].map(r => ({ value: r, label: r }))}} />
    </Row>
    <Row><h3>Zeit</h3></Row>
    <Row gutter={24}>
      <FormItem fieldName="begin" label="Beginn" type="date" />
      <FormItem fieldName="end" label="Ende" type="date" />
    </Row>
    <Row><h3>Sonstiges</h3></Row>
    <Row gutter={24}>
      <FormItem fieldName="employeeClosureIds" label="Abrechnungen" type="ref" typeData={{ query: getEmployeeClosuresQueryForEmployeeId, getId: c => c.id, render: c => `${c.id} ${c.title}`, multiple: true }} />
    </Row>
    { id !== 'new' && <>
      <Row><h3>Abwesenheit löschen</h3></Row>
      <div>
        <Space size="small">
          <ConfirmButton type="default" danger onClick={onDelete} confirmTitle={`Abwesenheit löschen?`} confirmContent={'Wollen Sie die Abwesenheit wirklich löschen?'}>
            Abwesenheit löschen
          </ConfirmButton>
        </Space>
      </div>
    </>}
  </AdvancedForm>
  </>)
}

export default EmployeeAbsenceDetails;

/*
<FormItem fieldName="employeeId" label="Mitarbeiter" readOnly={true} type="ref" typeData={{ query: useGetEmployeesQuery, getId: employee => employee.id, render: employee => `${employee.firstName} ${employee.lastName}`}} />
<FormItem fieldName="begin" label="Beginn" type="date" rules={[createRequiredRule('Beginn darf nicht leer sein')]} />
      <FormItem fieldName="endDate" label="Ende" type="date" />
      */